import React, { useRef, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

interface PuckProps {
  imageUrl: string | null;
  text: string;
  textColor: string;
  font: string;
  plasticColor: string;
}

function Puck({ imageUrl, text, textColor, font, plasticColor }: PuckProps) {
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Clear canvas with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);

    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Add text overlay if present
        if (text) {
          ctx.font = `bold 48px ${font.replace('font-', '')}`;
          ctx.fillStyle = textColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(text, canvas.width / 2, canvas.height / 2);
        }
        
        texture.needsUpdate = true;
      };
      img.src = imageUrl;
    } else if (text) {
      // If no image but text is present
      ctx.font = `bold 48px ${font.replace('font-', '')}`;
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
      texture.needsUpdate = true;
    }

    return texture;
  }, [imageUrl, text, textColor, font]);

  return (
    <group>
      {/* Puck body */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[1, 1, 0.25, 32]} />
        <meshPhongMaterial color={plasticColor} />
      </mesh>
      
      {/* Top face with custom design */}
      <mesh position={[0, 0.125, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.9, 32]} />
        <meshBasicMaterial map={texture} />
      </mesh>
    </group>
  );
}

export default function PuckPreview({ imageUrl, text, textColor, font, plasticColor }: PuckProps) {
  return (
    <div className="w-full h-[400px]">
      <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        <Puck
          imageUrl={imageUrl}
          text={text}
          textColor={textColor}
          font={font}
          plasticColor={plasticColor}
        />
        <OrbitControls enableZoom={false} />
      </Canvas>
    </div>
  );
}